import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import './MyProfile.css'
import './MyProfile-responsive.css'

import { Ripple } from "primereact/ripple";
import { Image } from 'primereact/image';
import { Toast } from 'primereact/toast';
import { DropdownProps } from 'primereact/dropdown';
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";

import TextInput from "../../Components/TextInput";
import ErrorPage from "../../Components/ErrorPage";
import { imageFileExtension } from "../../Api/FileConstant";
import { goToTop } from "../../Components/goToTop";

import { updateCompanyDetailForProfileApi, updateProfileImageApi, updateUserProfileApi, userProfile } from "../../Api/UserData";
import { baseURL } from "../../Api/ApiConstant";
import { checkEmail, checkPhoneNumber, checkText } from "../../Utils/Validator";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

interface Country {
    name: string;
    code: string;
    flag: string;
    phone_code: string;
}

const MyProfile: React.FC = () => {

    const toast = useRef<Toast>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState<boolean>(false);
    const [tab, setTab] = useState<string>('personal');
    const [editState, setEditState] = useState<boolean>(false);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [error, setError] = useState<string>('');
    const loadingPage = '';
    const USERID: string = localStorage.getItem('user_id') || "";
    const [userData, setUserData] = useState<any>([]);
    const [companyData, setCompanyData] = useState<any>([]);
    const defaultProfileImage = '/assets/images/no_data/no_profile_img.svg';

    const countries: Country[] = [
        {
            name: "United Kingdom",
            code: "GB",
            flag: "https://flagcdn.com/w320/gb.png",
            phone_code: "+44"
        },
        {
            name: "Sri Lanka",
            code: "LK",
            flag: "https://flagcdn.com/w320/lk.png",
            phone_code: "+94"
        },
        {
            name: "United States",
            code: "US",
            flag: "https://flagcdn.com/w320/us.png",
            phone_code: "+1"
        }
    ];

    /* ********************* Form variables *********************** */
    const [profileImage, setProfileImage] = useState<string | null>(null);

    /* Personal info */
    const [firstName, setFirstName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [addressLine1, setAddressLine1] = useState<string>('')
    const [addressLine2, setAddressLine2] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [postCode, setPostCode] = useState<string>('')
    const [country, setCountry] = useState<Country | null>(null);

    /* Company info */
    const [companyName, setCompanyName] = useState<string>('')
    const [companyRegNo, setCompanyRegNo] = useState<string>('')
    const [companyPhone, setCompanyPhone] = useState<string>('')
    const [companyEmail, setCompanyEmail] = useState<string>('')
    const [companyAddress, setCompanyAddress] = useState<string>('')
    const [companyCity, setCompanyCity] = useState<string>('')
    const [companyPostCode, setCompanyPostCode] = useState<string>('')

    /* Errors */
    const [profileImageError, setProfileImageError] = useState<string>('')

    const [firstNameError, setFirstNameError] = useState<string>('')
    const [lastNameError, setLastNameError] = useState<string>('')
    const [phoneError, setPhoneError] = useState<string>('')
    const [emailError, setEmailError] = useState<string>('')
    const [addressLine1Error, setAddressLine1Error] = useState<string>('')
    const [addressLine2Error, setAddressLine2Error] = useState<string>('')
    const [cityError, setCityError] = useState<string>('')
    const [postCodeError, setPostCodeError] = useState<string>('')
    const [countryError, setCountryError] = useState<string>('')

    const [companyNameError, setCompanyNameError] = useState<string>('')
    const [companyRegNoError, setCompanyRegNoError] = useState<string>('')
    const [companyPhoneError, setCompanyPhoneError] = useState<string>('')
    const [companyEmailError, setCompanyEmailError] = useState<string>('')
    const [companyAddressError, setCompanyAddressError] = useState<string>('')
    const [companyCityError, setCompanyCityError] = useState<string>('')
    const [companyPostCodeError, setCompanyPostCodeError] = useState<string>('')

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermissionCompanyDetails = useCanEdit('Site Menu', 'Company Details');


    /* ************************************************************ */

    const profileData = async () => {
        if (localStorage.getItem('user_id') && localStorage.getItem('token')) {
            const data = await userProfile(localStorage.getItem('token') || '');

            if (data.status) {

                setUserData(data.userData);
                setCompanyData(data.companyData);
            } else {
                setError('Something went wrong!');
            }
        } else {
            setError('Something went wrong!');
        }
    }

    const handleSetUserData = async () => {
        if (userData) {
            setProfileImage(userData.profile_image);
            setFirstName(userData.first_name);
            setLastName(userData.last_name);
            setPhone(userData.phone);
            setEmail(userData.email);
            setAddressLine1(userData.address_1);
            setAddressLine2(userData.address_2);
            setCity(userData.city);
            setPostCode(userData.post_code);
            setCountry(countries.find((c) => c.name === userData.country) || null);

            setCompanyName(companyData.company_name);
            setCompanyRegNo(companyData.company_regno);
            setCompanyPhone(companyData.company_phone);
            setCompanyEmail(companyData.company_email);
            setCompanyAddress(companyData.company_address);
            setCompanyPostCode(companyData.post_code);
            setCompanyCity(companyData.city);
        }
    }

    useEffect(() => {
        profileData();

    }, [])

    useEffect(() => {
        handleSetUserData();
    }, [userData, companyData])

    //For image upload/change
    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {

            const res = await updateProfileImageApi(Number(USERID), e.target.files[0]);

            if (res.status) {
                profileData();

                setSuccessMessage('Profile image updated successfully.');
                setShowAlert(true);
                setEditState(false);
                setTimeout(() => {
                    setSuccessMessage('');
                    setShowAlert(false);
                }, 2000);
                goToTop();
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Profile image updated failed',
                        detail: 'There was an error updating personal image.',
                        life: 3000
                    });
                }
            }
        }
    };

    const handleUpdatePersonalDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkProfileInfo()) {

            setLoading(true);
            const res = await updateUserProfileApi(Number(USERID), firstName, lastName, email, phone, addressLine1, addressLine2, city, postCode);
            setLoading(false);

            if (res.status) {
                profileData();

                setSuccessMessage('Personal details updated successfully.');
                setShowAlert(true);
                setEditState(false);
                setTimeout(() => {
                    setSuccessMessage('');
                    setShowAlert(false);
                }, 2000);
                goToTop();
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating personal details.',
                        life: 3000
                    });
                }
            }
        }
    }

    const handleUpdateCompanyDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCompanyInfo()) {
            setLoading(true);
            const res = await updateCompanyDetailForProfileApi(companyData.company_id, companyName, companyRegNo, companyEmail, companyPhone, companyAddress, companyPostCode, companyCity);
            setLoading(false);

            if (res.status) {
                profileData();

                setSuccessMessage('Company details updated successfully.');
                setShowAlert(true);
                setEditState(false);
                setTimeout(() => {
                    setSuccessMessage('');
                    setShowAlert(false);
                }, 2000);
                goToTop();
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating company details',
                        life: 3000
                    });
                }
            }

        }

    }



    const selectedCountryTemplate = (option: Country, props: DropdownProps) => {
        if (option) {
            return (
                <div className="d-flex gap-3 align-items-center">
                    <img alt={option.name} src={option.flag} className={`mr-2`} style={{ width: '25px', height: '15px' }} />
                    <div>{option.name}</div>
                </div>
            );
        }

        return <span>{props.placeholder}</span>;
    };

    const countryOptionTemplate = (option: Country) => {
        return (
            <div className="d-flex gap-3 align-items-center">
                <img alt={option.name} src={option.flag} className={`mr-2`} style={{ width: '25px', height: '15px' }} />
                <div>{option.name}</div>
            </div>
        );
    };

    const switchTabs = (tab: React.SetStateAction<string>) => {
        setTab(tab);
    };

    const goBack = () => {
        navigate(-1);
        goToTop();
    }

    const toggleEdit = () => {
        setEditState(!editState);
    };

    const handleCloseAlert = () => {
        setShowAlert(false);
    };


    function checkProfileInfo(): boolean {
        const is_firstName = checkText(firstName, true, setFirstNameError);
        const is_lastName = checkText(lastName, true, setLastNameError);
        const is_email = checkEmail(email, false, setEmailError);
        const is_phone = checkPhoneNumber(phone, true, setPhoneError);
        const is_addressLine1 = checkText(addressLine1, true, setAddressLine1Error);
        const is_addressLine2 = checkText(addressLine2, false, setAddressLine2Error);
        const is_city = checkText(city, true, setCityError);
        const is_postCode = checkText(postCode, true, setPostCodeError);

        /*if(country === null){
            setCountryError('This field is required')
        }*/



        return is_firstName && is_lastName && is_email && is_phone && is_addressLine1 && is_addressLine2 && is_city && is_postCode;
    }


    function checkCompanyInfo(): boolean {
        const is_companyName = checkText(companyName, true, setCompanyNameError);
        const is_companyRegNo = checkText(companyRegNo, true, setCompanyRegNoError);
        const is_companyPhone = checkPhoneNumber(companyPhone, true, setCompanyPhoneError);
        const is_companyEmail = checkEmail(companyEmail, false, setCompanyEmailError);
        const is_companyAddress = checkText(companyAddress, false, setCompanyAddressError);
        const is_companyCity = checkText(companyCity, false, setCompanyCityError);
        const is_companyPostCode = checkText(companyPostCode, false, setCompanyPostCodeError);


        return is_companyName && is_companyRegNo && is_companyPhone && is_companyEmail && is_companyAddress && is_companyCity && is_companyPostCode;
    }

    return (
        <React.Fragment>

            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingPage ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : error ? (
                    <ErrorPage errorMessage={error} />
                ) : (
                    <React.Fragment>
                        <div className="main_head_area">
                            <h4 className="main_heading">My profile</h4>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>

                        </div>
                        <div className="profile_page_area">
                            <div className="profile_page_head">
                                <h4>
                                    {tab === 'personal' ? (
                                        <React.Fragment>{editState ? 'Edit personla info' : 'My Details'}</React.Fragment>
                                    ) : tab === 'company' ? (
                                        <React.Fragment>{editState ? 'Edit company info' : 'Company Details'}</React.Fragment>
                                    ) : null}
                                </h4>
                                <button className="profile_edit_btn p-ripple" onClick={toggleEdit}>
                                    <i className={`bi ${editState ? 'bi-x-lg text-danger' : 'bi-pencil-square'}`}></i>
                                    <Ripple />
                                </button>
                            </div>

                            <div className="profile_page_body">
                                <div className="row">
                                    <div className="col-12 col-xl-4 mx-auto page_divider custom_page_height">
                                        <div className="profile_view_area mt-1 mt-sm-1 mb-3 mb-sm-0 pt-sm-2">
                                            {editState ? (
                                                <React.Fragment>
                                                    <div className="form_img_upload_area pt-0 pb-0">
                                                        <div className="form_img_upload">
                                                            <input
                                                                type="file"
                                                                accept={imageFileExtension}
                                                                className="form_img_upload_input"
                                                                id="profileImage"
                                                                onChange={handleImageChange}
                                                            />
                                                            <div className="form_img_upload_label_area">
                                                                <label
                                                                    htmlFor="profileImage"
                                                                    className={`form_img_upload_label ${profileImage ? 'uploaded' : ''}`}
                                                                    style={{ backgroundImage: profileImage ? `url(${baseURL}${profileImage})` : `` }}
                                                                >
                                                                    {!profileImage && (
                                                                        <div className="upload_icon">
                                                                            <i className="bi bi-camera-fill"></i>
                                                                        </div>
                                                                    )}
                                                                </label>


                                                                <label htmlFor="profileImage" className="upload_update_icon">
                                                                    <i className="bi bi-camera-fill"></i>
                                                                </label>

                                                            </div>

                                                        </div>
                                                    </div>
                                                    {profileImageError &&
                                                        <small className="form_error_msg text-center mt-2">{profileImageError}</small>
                                                    }
                                                </React.Fragment>
                                            ) : (
                                                <div className="profile_view_main_area">
                                                    {profileImage ? (
                                                        <Image
                                                            className="profile_img_area"
                                                            src={`${baseURL}${profileImage}`}
                                                            alt=""
                                                            preview
                                                        />
                                                    ) : (
                                                        <Image
                                                            className="profile_img_area p-sm-2 p-1"
                                                            src={defaultProfileImage}
                                                            alt=""
                                                        />
                                                    )}

                                                    {(userData?.first_name || userData?.last_name) && (
                                                        <h5 className="profile_name">
                                                            <React.Fragment>
                                                                {(userData?.first_name ? userData?.first_name : '') + (userData?.last_name ? (' ' + userData?.last_name) : '')}
                                                            </React.Fragment>
                                                        </h5>
                                                    )}

                                                    <h6 className="profile_role">
                                                        {userData?.type_name}
                                                    </h6>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12 col-xl-8">
                                        <div className="profile_view_area pt-3 mt-0 pb-0">
                                            <div className="profile_edit_tabs" id="profileEditTab" role="tablist" aria-orientation="vertical">
                                                <button
                                                    className={`profile_view_tab_btn p-ripple ${tab === 'personal' ? 'active' : ''}`}
                                                    onClick={() => switchTabs('personal')}
                                                    id="personal-detail-tab"
                                                    data-bs-toggle="pill"
                                                    data-bs-target="#personal-detail"
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="personal-detail"
                                                    aria-selected={tab === 'personal'}
                                                    disabled={editState ? true : false}
                                                >
                                                    Personal info
                                                    <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                                </button>

                                                {isPermissionCompanyDetails && (
                                                    <button
                                                        className={`profile_view_tab_btn p-ripple ${tab === 'company' ? 'active' : ''}`}
                                                        onClick={() => switchTabs('company')}
                                                        id="company-detail-tab"
                                                        data-bs-toggle="pill"
                                                        data-bs-target="#company-detail"
                                                        type="button"
                                                        role="tab"
                                                        aria-controls="company-detail"
                                                        aria-selected={tab === 'company'}
                                                        disabled={editState ? true : false}
                                                    >
                                                        Company info
                                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="tab-content profile_view_tab_content" id="profileEditTabContent">
                                                {/* Personal Details */}
                                                <div className={`tab-pane profile_view_tab_panel fade ${tab === 'personal' ? 'show active' : ''}`} id="personal-detail" role="tabpanel" aria-labelledby="personal-detail-tab" tabIndex={0}>
                                                    {editState ? (
                                                        <div className="row">
                                                            {/* First name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="firstName"
                                                                    label="First name"
                                                                    labelHtmlFor="firstName"
                                                                    required={true}
                                                                    value={firstName}
                                                                    placeholder="First name"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                                                                    error={firstNameError}
                                                                />
                                                            </div>

                                                            {/* Last name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="lastName"
                                                                    label="Last name"
                                                                    labelHtmlFor="lastName"
                                                                    required={true}
                                                                    value={lastName}
                                                                    placeholder="Last name"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                                                                    error={lastNameError}
                                                                />
                                                            </div>

                                                            {/* Phone */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="phone"
                                                                    label="Phone"
                                                                    labelHtmlFor="phone"
                                                                    required={true}
                                                                    value={phone}
                                                                    placeholder="Phone number"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                                                                    error={phoneError}
                                                                />
                                                            </div>

                                                            {/* Email */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="email"
                                                                    label="Email"
                                                                    labelHtmlFor="email"
                                                                    required={true}
                                                                    value={email}
                                                                    placeholder="Email"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                                                    error={emailError}
                                                                    inputDisabled={true}
                                                                    keyFilter={'email'}
                                                                />
                                                            </div>

                                                            {/* Address line 1 */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="addressLine1"
                                                                    label="Address line 1"
                                                                    labelHtmlFor="addressLine1"
                                                                    required={true}
                                                                    value={addressLine1}
                                                                    placeholder="Address line 1"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressLine1(e.target.value)}
                                                                    error={addressLine1Error}
                                                                />
                                                            </div>

                                                            {/* Address line 2 */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="addressLine2"
                                                                    label="Address line 2"
                                                                    labelHtmlFor="addressLine2"
                                                                    required={false}
                                                                    value={addressLine2}
                                                                    placeholder="Address line 2"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressLine2(e.target.value)}
                                                                    error={addressLine2Error}
                                                                />
                                                            </div>

                                                            {/* City */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="city"
                                                                    label="City"
                                                                    labelHtmlFor="city"
                                                                    required={true}
                                                                    value={city}
                                                                    placeholder="City"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
                                                                    error={cityError}
                                                                />
                                                            </div>

                                                            {/* Post code */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="postCode"
                                                                    label="Post code"
                                                                    labelHtmlFor="postCode"
                                                                    required={true}
                                                                    value={postCode}
                                                                    placeholder="Post code"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPostCode(e.target.value)}
                                                                    error={postCodeError}
                                                                />
                                                            </div>

                                                            {/* Country */}
                                                            {/*<div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label htmlFor="country" className="custom_form_label is_required">Country</label>
                                                                    <Dropdown value={country} id="country" onChange={(e: DropdownChangeEvent) => setCountry(e.value)}
                                                                        options={countries} optionLabel="name" valueTemplate={selectedCountryTemplate} itemTemplate={countryOptionTemplate}
                                                                        placeholder="Select country" className="form_dropdown w-100" showClear />

                                                                    {countryError !== '' &&
                                                                        <small className="form_error_msg">{countryError}</small>
                                                                    }
                                                                </div>
                                                            </div>*/}

                                                            <div className="col-12">
                                                                <div className="text-end mt-3">
                                                                    <Button
                                                                        label={`${loading ? 'Updating' : 'Update'}`}
                                                                        onClick={handleUpdatePersonalDetails}
                                                                        loading={loading}
                                                                        className="custom_btn primary" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="row">
                                                            {/* First name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">First name</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.first_name ? (
                                                                            <p>{userData.first_name}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">First name</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Last name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Last name</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.last_name ? (
                                                                            <p>{userData.last_name}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Last name</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Phone */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Phone</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.phone ? (
                                                                            <p>{userData.phone}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Phone</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Email */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Email</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.email ? (
                                                                            <p>{userData.email}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Email</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Address line 1 */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Address line 1</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.address_1 ? (
                                                                            <p>{userData.address_1}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Address line 1</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Address line 2 */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Address line 2</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.address_2 ? (
                                                                            <p>{userData.address_2}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Address line 2</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* City */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">City</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.city ? (
                                                                            <p>{userData.city}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">City</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Post code */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Post code</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.post_code ? (
                                                                            <p>{userData.post_code}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Post code</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Country */}
                                                            {/*<div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Country</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.country ? (
                                                                            <p>{userData.country}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Country</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>*/}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Company Details */}
                                                {isPermissionCompanyDetails && <div className={`tab-pane profile_view_tab_panel fade ${tab === 'company' ? 'show active' : ''}`} id="company-detail" role="tabpanel" aria-labelledby="company-detail-tab" tabIndex={0}>
                                                    {editState ? (
                                                        <div className="row">
                                                            {/* Company name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="companyName"
                                                                    label="Company name"
                                                                    labelHtmlFor="companyName"
                                                                    required={false}
                                                                    value={companyName}
                                                                    placeholder="Company name"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value)}
                                                                    error={companyNameError}
                                                                />
                                                            </div>

                                                            {/* Company reg no */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="companyRegNo"
                                                                    label="Company reg no"
                                                                    labelHtmlFor="companyRegNo"
                                                                    required={false}
                                                                    value={companyRegNo}
                                                                    placeholder="Company reg no"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyRegNo(e.target.value)}
                                                                    error={companyRegNoError}
                                                                />
                                                            </div>

                                                            {/* Phone */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="companyPhone"
                                                                    label="Phone"
                                                                    labelHtmlFor="companyPhone"
                                                                    required={false}
                                                                    value={companyPhone}
                                                                    placeholder="Phone"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyPhone(e.target.value)}
                                                                    error={companyPhoneError}
                                                                />
                                                            </div>

                                                            {/* Email */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="companyEmail"
                                                                    label="Email"
                                                                    labelHtmlFor="companyEmail"
                                                                    required={false}
                                                                    value={companyEmail}
                                                                    placeholder="Email"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyEmail(e.target.value)}
                                                                    error={companyEmailError}
                                                                    keyFilter={'email'}
                                                                />
                                                            </div>

                                                            {/* Address */}
                                                            <div className="col-12">
                                                                <TextInput
                                                                    id="companyAddress"
                                                                    label="Address"
                                                                    labelHtmlFor="companyAddress"
                                                                    required={false}
                                                                    value={companyAddress}
                                                                    placeholder="Address"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyAddress(e.target.value)}
                                                                    error={companyAddressError}
                                                                />
                                                            </div>

                                                            {/* Post code */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="companyPostCode"
                                                                    label="Post code"
                                                                    labelHtmlFor="companyPostCode"
                                                                    required={false}
                                                                    value={companyPostCode}
                                                                    placeholder="Post code"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyPostCode(e.target.value)}
                                                                    error={companyPostCodeError}
                                                                />
                                                            </div>

                                                            {/* City */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="companyCity"
                                                                    label="City"
                                                                    labelHtmlFor="companyCity"
                                                                    required={false}
                                                                    value={companyCity}
                                                                    placeholder="City"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyCity(e.target.value)}
                                                                    error={companyCityError}
                                                                />
                                                            </div>

                                                            <div className="col-12">
                                                                <div className="text-end mt-3">
                                                                    <Button
                                                                        label={`${loading ? 'Updating' : 'Update'}`}
                                                                        onClick={handleUpdateCompanyDetails}
                                                                        loading={loading}
                                                                        className="custom_btn primary" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="row">
                                                            {/* Company name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Company name</label>
                                                                    <div className="form_data_view_box">
                                                                        {companyData?.company_name ? (
                                                                            <p>{companyData.company_name}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Company name</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Company reg no */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Company reg no</label>
                                                                    <div className="form_data_view_box">
                                                                        {companyData?.company_regno ? (
                                                                            <p>{companyData.company_regno}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Company reg no</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Phone */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Phone</label>
                                                                    <div className="form_data_view_box">
                                                                        {companyData?.company_phone ? (
                                                                            <p>{companyData.company_phone}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Phone</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Email */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Email</label>
                                                                    <div className="form_data_view_box">
                                                                        {companyData?.company_email ? (
                                                                            <p>{companyData.company_email}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Email</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Address */}
                                                            <div className="col-12">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Address</label>
                                                                    <div className="form_data_view_box">
                                                                        {companyData?.company_address ? (
                                                                            <p>{companyData.company_address}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Address</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Post code */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Post code</label>
                                                                    <div className="form_data_view_box">
                                                                        {companyData?.post_code ? (
                                                                            <p>{companyData.post_code}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">Post code</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* City */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">City</label>
                                                                    <div className="form_data_view_box">
                                                                        {companyData?.city ? (
                                                                            <p>{companyData.city}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text">City</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    )}
                                                </div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>

            {/* Show success message */}
            <div className={`message_backdrop ${showAlert ? 'show' : ''}`} onClick={handleCloseAlert}>
                <div className={`message_box ${showAlert ? 'show' : ''}`} onClick={e => e.stopPropagation()}>
                    <div className="message_box_content">
                        <i className="bi bi-check-circle-fill message_box_icon"></i>
                        <h6 className="message_box_message">
                            {successMessage}
                        </h6>
                    </div>
                </div>
            </div>
            {/*  */}
        </React.Fragment>
    )
};

export default MyProfile;